/*
   MODULES (professor / admin)

   A module groups an assignment or several with the resources that belong with
   it: an answer-key PDF, a solution workbook, a teaching guide, links to videos.
   This view is where they are built, and where a module is poured into a class.

   Deliberately its OWN view rather than a section bolted into class-detail.js.
   That file is 926 lines and is the page FIN 413 and FIN 397 use in production
   every week; a new component next to it has a blast radius of one file, and if
   the platform merge lands elsewhere the whole feature deletes cleanly.

   ⚠️ Audience is never defaulted to "student" anywhere in this file. Making a
   resource student-visible is always an explicit act by the person uploading it,
   because the failure mode of the opposite default is publishing an answer key.
*/

import { api, SERVER } from "../scripts/api.js";

const esc = (s) =>
   String(s ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
   );

const KIND_LABEL = {
   pdf: "PDF",
   workbook: "Workbook",
   video_link: "Video",
   link: "Link",
};

const MODULE_KIND_LABEL = {
   case: "Case",
   warmup: "Warm-up",
   speed: "Speed challenge",
   combine: "Combine",
   other: "Other",
};

// datetime-local gives "YYYY-MM-DDTHH:mm" with no zone, which new Date() reads as
// LOCAL time. That is what we want: a professor typing 9am means 9am where they
// are. toISOString() then converts to UTC for the wire.
const toIso = (v) => (v ? new Date(v).toISOString() : null);

const fmtRelease = (iso) =>
   iso ? new Date(iso).toLocaleString() : "Not released";

// 2026-09-16: release is a RULE, not only a date (lib/resource-release.js on the
// server). "Now" is stored as 'at' with the current time.
const RULE_LABEL = {
   after_run: "After the timed run",
   after_due: "After the due date",
   at: "On a date",
   never: "Never",
};

// datetime-local value for an ISO timestamp, in the viewer's local time.
const toLocalInput = (iso) => {
   if (!iso) return "";
   const d = new Date(iso);
   const pad = (n) => String(n).padStart(2, "0");
   return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const describeRelease = (r) =>
   r.release_rule === "at" || !r.release_rule
      ? fmtRelease(r.release_at)
      : RULE_LABEL[r.release_rule] || r.release_rule;

class AppModules extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <section id="modules">
            <div class="page-head">
               <h1>Modules</h1>
               <p class="sub">Group an assignment with the resources that go with it, then add the module to a class.</p>
            </div>

            <div class="cd-section">
               <div class="cd-section-head">
                  <h2>Your modules</h2>
                  <button type="button" class="btn-primary" id="btn-new-module">New module</button>
               </div>
               <div id="mod-list" class="cd-panel"><p class="empty">Loading...</p></div>
            </div>

            <div class="cd-section" id="mod-detail-section" hidden>
               <div class="cd-section-head">
                  <h2 id="mod-detail-title">Module</h2>
                  <button type="button" id="btn-close-module">Close</button>
               </div>
               <div class="cd-panel">
                  <p class="sub" id="mod-detail-sub"></p>

                  <h3>Resources</h3>
                  <div id="mod-resources"></div>

                  <form id="add-resource-form" class="stack">
                     <h3>Add a resource</h3>
                     <label>Title
                        <input type="text" name="title" required placeholder="Solutions guide">
                     </label>
                     <label>Type
                        <select name="kind" id="res-kind">
                           <option value="pdf">PDF (upload)</option>
                           <option value="workbook">Workbook (upload)</option>
                           <option value="video_link">Video (link)</option>
                           <option value="link">Link</option>
                        </select>
                     </label>
                     <label id="res-file-label">File
                        <input type="file" name="file" id="res-file">
                     </label>
                     <label id="res-url-label" hidden>URL
                        <input type="url" name="url" id="res-url" placeholder="https://...">
                     </label>
                     <label>Who can see it
                        <select name="audience" id="res-audience">
                           <option value="instructor">Instructors only</option>
                           <option value="student">Students</option>
                        </select>
                     </label>
                     <label id="res-rule-label" hidden>Release to students
                        <select name="release_rule" id="res-rule">
                           <option value="at">On a date</option>
                           <option value="after_run">After the timed run</option>
                           <option value="after_due">After the due date</option>
                           <option value="never">Never</option>
                        </select>
                     </label>
                     <label id="res-release-label" hidden>Release to students at
                        <input type="datetime-local" name="release_at" id="res-release">
                        <span class="hint">Leave blank to hold it back. You can release it later, or per class.</span>
                     </label>
                     <button type="submit" class="btn-primary">Add resource</button>
                     <p class="msg" id="add-resource-msg"></p>
                  </form>

                  <h3>Assignments in this module</h3>
                  <div id="mod-assignments"></div>
                  <form id="link-assignment-form" class="stack">
                     <label>Add an assignment
                        <select id="link-assignment-select"><option value="">Loading...</option></select>
                     </label>
                     <button type="submit">Link assignment</button>
                     <p class="msg" id="link-assignment-msg"></p>
                  </form>

                  <h3>Add this module to a class</h3>
                  <form id="adopt-form" class="stack">
                     <label>Class
                        <select id="adopt-class-select"><option value="">Loading...</option></select>
                     </label>
                     <button type="submit" class="btn-primary">Add to class</button>
                     <p class="msg" id="adopt-msg"></p>
                  </form>
               </div>
            </div>

            <dialog id="new-module-dialog">
               <form id="new-module-form" method="dialog" class="stack">
                  <h2>New module</h2>
                  <label>Title
                     <input type="text" name="title" id="nm-title" required placeholder="Laundry Time">
                  </label>
                  <label>Slug
                     <input type="text" name="slug" id="nm-slug" required placeholder="laundry-time">
                     <span class="hint">Lowercase and hyphens. If this is a catalog case, use the Case-Catalog slug exactly.</span>
                  </label>
                  <label>Kind
                     <select name="kind" id="nm-kind">
                        <option value="case">Case</option>
                        <option value="warmup">Warm-up</option>
                        <option value="speed">Speed challenge</option>
                        <option value="combine">Combine</option>
                        <option value="other">Other</option>
                     </select>
                  </label>
                  <label>Description
                     <textarea name="description" id="nm-desc" rows="2"></textarea>
                  </label>
                  <p class="msg" id="new-module-msg"></p>
                  <menu>
                     <button type="button" id="btn-cancel-module">Cancel</button>
                     <button type="submit" class="btn-primary">Create</button>
                  </menu>
               </form>
            </dialog>
         </section>
      `;

      this.dialog = this.querySelector("#new-module-dialog");
      this.querySelector("#btn-new-module").addEventListener("click", () => {
         this.querySelector("#new-module-form").reset();
         this.msg("#new-module-msg", "");
         this.dialog.showModal();
      });
      this.querySelector("#btn-cancel-module").addEventListener("click", () => this.dialog.close());
      this.querySelector("#new-module-form").addEventListener("submit", (e) => this.onCreateModule(e));
      this.querySelector("#btn-close-module").addEventListener("click", () => {
         this.querySelector("#mod-detail-section").hidden = true;
         this.openModuleId = null;
      });

      // Title -> slug, only while the slug is untouched, so a deliberate slug is
      // never overwritten by a later title edit.
      const slugInput = this.querySelector("#nm-slug");
      slugInput.addEventListener("input", () => { slugInput.dataset.touched = "1"; });
      this.querySelector("#nm-title").addEventListener("input", (e) => {
         if (slugInput.dataset.touched) return;
         slugInput.value = String(e.target.value).toLowerCase()
            .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      });

      this.querySelector("#res-kind").addEventListener("change", () => this.syncResourceForm());
      this.querySelector("#res-audience").addEventListener("change", () => this.syncResourceForm());
      this.querySelector("#res-rule").addEventListener("change", () => this.syncResourceForm());
      this.querySelector("#add-resource-form").addEventListener("submit", (e) => this.onAddResource(e));
      this.querySelector("#link-assignment-form").addEventListener("submit", (e) => this.onLinkAssignment(e));
      this.querySelector("#adopt-form").addEventListener("submit", (e) => this.onAdopt(e));
      this.syncResourceForm();
   }

   async show() { await this.loadModules(); }

   msg(sel, text, kind = "") {
      const el = this.querySelector(sel);
      if (!el) return;
      el.textContent = text;
      el.className = `msg${kind ? " " + kind : ""}`;
   }

   // The file input and the URL input are mutually exclusive, and the release
   // date only means anything for a student-audience resource.
   syncResourceForm() {
      const kind = this.querySelector("#res-kind").value;
      const isUrl = kind === "video_link" || kind === "link";
      this.querySelector("#res-file-label").hidden = isUrl;
      this.querySelector("#res-url-label").hidden = !isUrl;
      this.querySelector("#res-file").required = !isUrl;
      this.querySelector("#res-url").required = isUrl;
      const forStudents = this.querySelector("#res-audience").value === "student";
      this.querySelector("#res-rule-label").hidden = !forStudents;
      this.querySelector("#res-release-label").hidden =
         !forStudents || this.querySelector("#res-rule").value !== "at";
   }

   async loadModules() {
      const el = this.querySelector("#mod-list");
      try {
         const res = await api("/modules");
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error("failed");
         const mods = data.modules || [];
         if (!mods.length) {
            el.innerHTML = `<p class="empty">No modules yet. Create one to start grouping resources.</p>`;
            return;
         }
         el.innerHTML = `
            <ul class="cd-list">
               ${mods.map((m) => `
                  <li class="cd-item">
                     <div class="cd-item-main">
                        <div class="cd-item-name">${esc(m.title)}</div>
                        <div class="cd-item-meta">
                           ${esc(MODULE_KIND_LABEL[m.kind] || m.kind)} · ${esc(m.slug)} ·
                           ${Number(m.assignment_count)} assignment${Number(m.assignment_count) === 1 ? "" : "s"} ·
                           ${Number(m.resource_count)} resource${Number(m.resource_count) === 1 ? "" : "s"}
                        </div>
                     </div>
                     <div class="cd-item-actions">
                        <button type="button" class="btn-open-module" data-id="${m.module_id}">Open</button>
                     </div>
                  </li>`).join("")}
            </ul>`;
         el.querySelectorAll(".btn-open-module").forEach((b) =>
            b.addEventListener("click", () => this.openModule(Number(b.dataset.id))));
      } catch {
         el.innerHTML = `<p class="msg err">Couldn't load modules.</p>`;
      }
   }

   async onCreateModule(e) {
      e.preventDefault();
      const body = {
         title: this.querySelector("#nm-title").value.trim(),
         slug: this.querySelector("#nm-slug").value.trim(),
         kind: this.querySelector("#nm-kind").value,
         description: this.querySelector("#nm-desc").value.trim() || null,
      };
      try {
         const res = await api("/modules", { method: "POST", body: JSON.stringify(body) });
         const data = await res.json();
         if (!res.ok || !data.ok) {
            this.msg("#new-module-msg", `Couldn't create it: ${data.reason || "error"}`, "err");
            return;
         }
         this.dialog.close();
         await this.loadModules();
         await this.openModule(data.module.module_id);
      } catch {
         this.msg("#new-module-msg", "Couldn't create it.", "err");
      }
   }

   async openModule(moduleId) {
      this.openModuleId = moduleId;
      const section = this.querySelector("#mod-detail-section");
      section.hidden = false;
      this.msg("#add-resource-msg", "");
      try {
         const res = await api(`/modules/${moduleId}`);
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error("failed");
         this.querySelector("#mod-detail-title").textContent = data.module.title;
         this.querySelector("#mod-detail-sub").textContent =
            `${MODULE_KIND_LABEL[data.module.kind] || data.module.kind} · ${data.module.slug}` +
            (data.module.description ? ` — ${data.module.description}` : "");
         this.renderResources(data.resources || []);
         this.renderAssignments(data.assignments || []);
         await Promise.all([this.loadAssignmentOptions(), this.loadClassOptions()]);
         section.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch {
         this.querySelector("#mod-resources").innerHTML = `<p class="msg err">Couldn't load this module.</p>`;
      }
   }

   renderResources(resources) {
      const el = this.querySelector("#mod-resources");
      if (!resources.length) {
         el.innerHTML = `<p class="empty">No resources yet.</p>`;
         return;
      }
      el.innerHTML = `
         <ul class="cd-list">
            ${resources.map((r) => `
               <li class="cd-item">
                  <div class="cd-item-main">
                     <div class="cd-item-name">${esc(r.title)}</div>
                     <div class="cd-item-meta">
                        ${esc(KIND_LABEL[r.kind] || r.kind)} ·
                        ${r.audience === "student"
                           ? `Students · ${esc(describeRelease(r))}`
                           : `<span class="cd-warn">Instructors only</span>`}
                        ${r.filename ? ` · ${esc(r.filename)}` : ""}
                        ${r.auto_key ? ` · <span class="hint">added automatically</span>` : ""}
                     </div>
                  </div>
                  <div class="cd-item-actions">
                     ${r.audience === "student" ? `
                        <select class="sel-release-rule" data-id="${r.resource_id}" aria-label="When students get it">
                           ${["after_run", "after_due", "at", "never"].map((k) =>
                              `<option value="${k}" ${(r.release_rule || "at") === k ? "selected" : ""}>${RULE_LABEL[k]}</option>`).join("")}
                           <option value="now">Release now</option>
                        </select>
                        <input type="datetime-local" class="inp-release-at" data-id="${r.resource_id}"
                               value="${esc(toLocalInput(r.release_at))}"
                               ${(r.release_rule || "at") === "at" ? "" : "hidden"}>` : ""}
                     <button type="button" class="btn-open-resource" data-id="${r.resource_id}" data-url="${esc(r.url || "")}">Open</button>
                     <button type="button" class="btn-toggle-audience" data-id="${r.resource_id}" data-audience="${esc(r.audience)}">
                        ${r.audience === "student" ? "Make instructor only" : "Share with students"}
                     </button>
                     <button type="button" class="btn-del-resource" data-id="${r.resource_id}">Delete</button>
                  </div>
               </li>`).join("")}
         </ul>`;

      el.querySelectorAll(".btn-open-resource").forEach((b) =>
         b.addEventListener("click", () => this.onOpenResource(Number(b.dataset.id), b.dataset.url)));
      el.querySelectorAll(".btn-toggle-audience").forEach((b) =>
         b.addEventListener("click", () => this.onToggleAudience(Number(b.dataset.id), b.dataset.audience)));
      el.querySelectorAll(".btn-del-resource").forEach((b) =>
         b.addEventListener("click", () => this.onDeleteResource(Number(b.dataset.id))));
      el.querySelectorAll(".sel-release-rule").forEach((sel) =>
         sel.addEventListener("change", () => this.onReleaseRule(Number(sel.dataset.id), sel.value)));
      el.querySelectorAll(".inp-release-at").forEach((inp) =>
         inp.addEventListener("change", () => this.onReleaseDate(Number(inp.dataset.id), inp.value)));
   }

   /*
      2026-09-16. The module DEFAULT for every class this module is in. "Release
      now" is stored as 'at' with the current time. Choosing "On a date" keeps any
      date already set and shows the picker; an empty date means withheld.
   */
   async onReleaseRule(rid, value) {
      const body = value === "now"
         ? { release_rule: "at", release_at: new Date().toISOString() }
         : { release_rule: value };
      await this.patchResource(rid, body, "Couldn't change when students get this.");
   }

   async onReleaseDate(rid, raw) {
      await this.patchResource(rid, { release_rule: "at", release_at: raw ? toIso(raw) : null },
         "Couldn't set the release date.");
   }

   async patchResource(rid, body, failMsg) {
      try {
         const res = await api(`/modules/${this.openModuleId}/resources/${rid}`, {
            method: "PATCH", body: JSON.stringify(body),
         });
         if (!res.ok) throw new Error("failed");
         await this.openModule(this.openModuleId);
      } catch {
         alert(failMsg);
      }
   }

   renderAssignments(assignments) {
      const el = this.querySelector("#mod-assignments");
      if (!assignments.length) {
         el.innerHTML = `<p class="empty">No assignments linked. A module can hold resources only.</p>`;
         return;
      }
      el.innerHTML = `
         <ul class="cd-list">
            ${assignments.map((a) => `
               <li class="cd-item">
                  <div class="cd-item-main">
                     <div class="cd-item-name">${esc(a.title)}</div>
                     <div class="cd-item-meta">${esc(a.assignment_code)}</div>
                  </div>
                  <div class="cd-item-actions">
                     <button type="button" class="btn-unlink-assignment" data-id="${a.assignment_id}">Remove</button>
                  </div>
               </li>`).join("")}
         </ul>`;
      el.querySelectorAll(".btn-unlink-assignment").forEach((b) =>
         b.addEventListener("click", () => this.onUnlinkAssignment(Number(b.dataset.id))));
   }

   /*
      Upload posts raw FormData with fetch, NOT through api(), because api() sets
      Content-Type: application/json on every call. On a multipart body that
      overrides the boundary the browser would otherwise generate, and the server
      parses zero fields.
   */
   async onAddResource(e) {
      e.preventDefault();
      if (!this.openModuleId) return;
      const form = e.target;
      const btn = form.querySelector("button[type=submit]");
      const kind = this.querySelector("#res-kind").value;
      const audience = this.querySelector("#res-audience").value;
      const releaseRaw = this.querySelector("#res-release").value;
      const releaseRule = this.querySelector("#res-rule").value;

      btn.disabled = true;
      this.msg("#add-resource-msg", "Adding...");
      try {
         let res;
         if (kind === "pdf" || kind === "workbook") {
            const fd = new FormData();
            fd.append("kind", kind);
            fd.append("title", form.title.value.trim());
            fd.append("audience", audience);
            if (audience === "student") fd.append("release_rule", releaseRule);
            if (audience === "student" && releaseRule === "at" && releaseRaw) fd.append("release_at", toIso(releaseRaw));
            fd.append("file", this.querySelector("#res-file").files[0]);
            res = await fetch(`${SERVER}/modules/${this.openModuleId}/resources`, {
               method: "POST", body: fd, credentials: "include",
            });
         } else {
            res = await api(`/modules/${this.openModuleId}/resources`, {
               method: "POST",
               body: JSON.stringify({
                  kind,
                  title: form.title.value.trim(),
                  audience,
                  url: this.querySelector("#res-url").value.trim(),
                  release_rule: audience === "student" ? releaseRule : "at",
                  release_at: audience === "student" && releaseRule === "at" && releaseRaw ? toIso(releaseRaw) : null,
               }),
            });
         }
         const data = await res.json();
         if (!res.ok || !data.ok) {
            this.msg("#add-resource-msg", `Couldn't add it: ${data.reason || "error"}`, "err");
            return;
         }
         form.reset();
         this.syncResourceForm();
         this.msg("#add-resource-msg", "Added.", "ok");
         await this.openModule(this.openModuleId);
         await this.loadModules();
      } catch {
         this.msg("#add-resource-msg", "Couldn't add it.", "err");
      } finally {
         btn.disabled = false;
      }
   }

   /*
      Flipping to student-visible is the one destructive-by-omission action here,
      so it confirms. Flipping back to instructor-only does not: withdrawing
      something is always safe.
   */
   async onToggleAudience(rid, current) {
      const next = current === "student" ? "instructor" : "student";
      if (next === "student" &&
          !confirm("Share this with students?\n\nIt becomes visible to every student in any class that has this module, once its release time passes. Answer keys should stay instructor only.")) {
         return;
      }
      try {
         const res = await api(`/modules/${this.openModuleId}/resources/${rid}`, {
            method: "PATCH", body: JSON.stringify({ audience: next }),
         });
         if (!res.ok) throw new Error("failed");
         await this.openModule(this.openModuleId);
      } catch {
         alert("Couldn't change who can see this.");
      }
   }

   async onDeleteResource(rid) {
      if (!confirm("Delete this resource? This cannot be undone.\n\nIf it was added automatically when the assignment was seeded, rebuilding the assignment will not bring it back.")) return;
      try {
         const res = await api(`/modules/${this.openModuleId}/resources/${rid}`, { method: "DELETE" });
         if (!res.ok) throw new Error("failed");
         await this.openModule(this.openModuleId);
         await this.loadModules();
      } catch {
         alert("Couldn't delete it.");
      }
   }

   async onOpenResource(rid, url) {
      if (url) { window.open(url, "_blank", "noopener"); return; }
      try {
         const res = await fetch(`${SERVER}/modules/${this.openModuleId}/resources/${rid}/download`,
            { credentials: "include" });
         if (!res.ok) { alert("Couldn't open it."); return; }
         const blob = await res.blob();
         const href = URL.createObjectURL(blob);
         window.open(href, "_blank", "noopener");
         // Revoke late: revoking immediately can race the new tab's load.
         setTimeout(() => URL.revokeObjectURL(href), 60000);
      } catch {
         alert("Couldn't open it.");
      }
   }

   async loadAssignmentOptions() {
      const sel = this.querySelector("#link-assignment-select");
      try {
         const res = await api("/assignments-list");
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error("failed");
         sel.innerHTML = `<option value="">Choose an assignment…</option>` +
            (data.assignments || []).map((a) =>
               `<option value="${a.assignment_id}">${esc(a.title)} (${esc(a.assignment_code)})</option>`).join("");
      } catch {
         // /assignments-list gates on the GLOBAL professor role, so a TA whose
         // user_table.role is 'student' gets nothing here. That is a pre-existing
         // limitation of that route, not of modules; say so rather than showing
         // an empty picker that looks broken.
         sel.innerHTML = `<option value="">Couldn't load assignments</option>`;
      }
   }

   async loadClassOptions() {
      const sel = this.querySelector("#adopt-class-select");
      try {
         const res = await api("/list-classes");
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error("failed");
         sel.innerHTML = `<option value="">Choose a class…</option>` +
            (data.classes || []).map((c) =>
               `<option value="${c.class_id}">${esc(c.course_code)} · ${esc(c.section)}</option>`).join("");
      } catch {
         sel.innerHTML = `<option value="">Couldn't load classes</option>`;
      }
   }

   async onLinkAssignment(e) {
      e.preventDefault();
      const id = Number(this.querySelector("#link-assignment-select").value);
      if (!Number.isInteger(id) || !id) {
         this.msg("#link-assignment-msg", "Choose an assignment first.", "err");
         return;
      }
      try {
         const res = await api(`/modules/${this.openModuleId}/assignments`, {
            method: "POST", body: JSON.stringify({ assignment_id: id }),
         });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error("failed");
         this.msg("#link-assignment-msg", "Linked.", "ok");
         await this.openModule(this.openModuleId);
         await this.loadModules();
      } catch {
         this.msg("#link-assignment-msg", "Couldn't link it.", "err");
      }
   }

   async onUnlinkAssignment(id) {
      try {
         const res = await api(`/modules/${this.openModuleId}/assignments/${id}`, { method: "DELETE" });
         if (!res.ok) throw new Error("failed");
         await this.openModule(this.openModuleId);
         await this.loadModules();
      } catch {
         alert("Couldn't remove it.");
      }
   }

   async onAdopt(e) {
      e.preventDefault();
      const classId = Number(this.querySelector("#adopt-class-select").value);
      if (!Number.isInteger(classId) || !classId) {
         this.msg("#adopt-msg", "Choose a class first.", "err");
         return;
      }
      try {
         const res = await api(`/classes/${classId}/modules/${this.openModuleId}/adopt`, { method: "POST" });
         const data = await res.json();
         if (!res.ok || !data.ok) {
            this.msg("#adopt-msg", `Couldn't add it: ${data.reason || "error"}`, "err");
            return;
         }
         const n = Number(data.assignments_attached || 0);
         this.msg("#adopt-msg",
            `Added. ${n} assignment${n === 1 ? "" : "s"} attached to the class.`, "ok");
      } catch {
         this.msg("#adopt-msg", "Couldn't add it to the class.", "err");
      }
   }
}

customElements.define("app-modules", AppModules);
